"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { toUserMessage } from "@/shared/api/errorMessages";
import { FieldError } from "@/shared/form/FieldError";
import type { FieldErrors } from "@/shared/form/types";
import { validateFields } from "@/shared/form/validate";

import { AuthCard } from "@/features/auth/components/AuthCard";
import { AuthHeader } from "@/features/auth/components/AuthHeader";
import { KakaoLoginButton } from "@/features/auth/components/KakaoLoginButton";
import { useEmailLogin } from "@/features/auth/queries/useLogin";
import { loginSchema } from "@/features/auth/schemas/login";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function BuyerLoginPage() {
  return (
    <Suspense>
      <BuyerLoginForm />
    </Suspense>
  );
}

function BuyerLoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "/";

  const emailLogin = useEmailLogin();

  const [fields, setFields] = useState({ email: "", password: "" });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const isAnyPending = emailLogin.isPending;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    const next = { ...fields, [name]: value };
    setFields(next);
    const { errors } = validateFields(loginSchema, next);
    setFieldErrors(errors ?? {});
    if (authError) setAuthError(null);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { data, errors } = validateFields(loginSchema, fields);
    if (errors) {
      setFieldErrors(errors);
      return;
    }
    setAuthError(null);
    emailLogin.mutate(
      { email: data.email, password: data.password },
      {
        onSuccess: () => {
          toast.success("로그인되었습니다");
          router.push(returnUrl);
        },
        onError: (err) => setAuthError(toUserMessage(err, "로그인에 실패했습니다")),
      },
    );
  }

  return (
    <AuthCard size="md">
      <AuthHeader title="로그인" subtitle="도매 플랫폼에 로그인하세요" />

      {authError && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{authError}</span>
        </div>
      )}

      <KakaoLoginButton disabled={isAnyPending} />

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-neutral-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-xs text-muted-foreground">또는</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
            이메일
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="email@example.com"
            className="h-10 rounded-md"
            value={fields.email}
            onChange={handleChange}
            disabled={isAnyPending}
          />
          {fieldErrors.email && <FieldError message={fieldErrors.email[0]} />}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm font-medium text-neutral-700">
            비밀번호
          </Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="비밀번호를 입력하세요"
              className="h-10 rounded-md pr-10"
              value={fields.password}
              onChange={handleChange}
              disabled={isAnyPending}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {fieldErrors.password && <FieldError message={fieldErrors.password[0]} />}
        </div>
        <Button
          type="submit"
          className="h-10 w-full bg-primary font-medium text-white hover:bg-primary/90"
          disabled={isAnyPending}
        >
          {emailLogin.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          로그인
        </Button>
      </form>

      <div className="mt-5 flex items-center justify-between">
        <Link
          href="/signup"
          className="text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          회원가입
        </Link>
        <Link
          href="/forgot-password"
          className="text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          비밀번호 찾기
        </Link>
      </div>
    </AuthCard>
  );
}
